import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import MenuOutlined from "@mui/icons-material/MenuOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import CSFSidebar from "../../components/SidebarQuestionnaire/CSFSidebar/CSFSidebar";
import CRRSidebar from "../../components/SidebarQuestionnaire/CRRSidebar/CRRSidebar";
import CSFSurvey from "./Survey-CSF/component";
import CRRSurvey from "./Survey-CRR/component";
import { useDispatch, useSelector } from "react-redux";
import {
  checkIsAdmin,
  getLocalStorage,
  getValidLandingScreen,
  setLocalStorage,
} from "../../helpers/utils";
import { SURVEYS } from "../../helpers/constants";
import { setActiveSurvey } from "../../slices/signIn.slice";
import { Button } from "../../components/ds";
import "../../components/SidebarQuestionnaire/SidebarQuestionnaire.scss";

const SurveyHome = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isAdminLogin = checkIsAdmin();
  const selectedSurvey = getLocalStorage("SELECTED-SURVEY");
  const activeSurveyLS = getLocalStorage("ACTIVE-SURVEY-TYPE");
  const { activeSurvey: activeSurveyStore } = useSelector(
    (state) => state.signIn
  );
  const { surveyEditable } = useSelector((state) => state.surveyList);
  const [domainNavOpen, setDomainNavOpen] = useState(false);

  const hasValidCSFSurvey = useMemo(
    () => Boolean(selectedSurvey?.csf_survey_id),
    [selectedSurvey]
  );

  const hasSelectedSurvey = useMemo(
    () => Boolean(selectedSurvey && (selectedSurvey.id || selectedSurvey.csf_survey_id)),
    [selectedSurvey]
  );

  useEffect(() => {
    // syncing localstorage and store values
    if (!activeSurveyLS)
      setLocalStorage("ACTIVE-SURVEY-TYPE", activeSurveyStore);
    else if (activeSurveyLS !== activeSurveyStore)
      dispatch(setActiveSurvey(activeSurveyLS));
    return () => {
      dispatch(setActiveSurvey(SURVEYS.CSF));
    };
  }, []);

  // Close domain drawer when switching CSF/CRR or escaping.
  useEffect(() => {
    setDomainNavOpen(false);
  }, [activeSurveyStore]);

  useEffect(() => {
    if (!domainNavOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setDomainNavOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [domainNavOpen]);

  const handleCSFSelect = () => {
    dispatch(setActiveSurvey(SURVEYS.CSF));
  };
  const handleCRRSelect = () => {
    dispatch(setActiveSurvey(SURVEYS.CRR));
  };

  const exitAssessment = () => {
    if (isAdminLogin) {
      history.push("/admin-survey-list");
      return;
    }
    history.push(getValidLandingScreen() || "/dashboard");
  };

  const closeDomainNav = () => setDomainNavOpen(false);

  if (!hasSelectedSurvey) {
    return (
      <div className="survey-empty">
        <div className="survey-empty-card">
          <p className="survey-empty-eyebrow">Assessments</p>
          <h1 className="survey-empty-title">No assessment selected</h1>
          <p className="survey-empty-copy">
            Open an assessment from your dashboard (or the survey list) so domains
            and questions can load. The main navigation stays available on the left.
          </p>
          <div className="survey-empty-actions">
            <Button variant="primary" onClick={exitAssessment}>
              {isAdminLogin ? "Back to survey list" : "Back to dashboard"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="sidebar-qn-container sidebar-qn-container--in-shell"
      data-domain-nav={domainNavOpen ? "true" : "false"}
    >
      <div className="survey-exit-bar">
        <Button
          variant="secondary"
          size="sm"
          className="survey-domain-toggle"
          aria-expanded={domainNavOpen}
          aria-controls="survey-domain-nav"
          onClick={() => setDomainNavOpen((v) => !v)}
          leftIcon={domainNavOpen ? <CloseOutlined fontSize="small" /> : <MenuOutlined fontSize="small" />}
        >
          Domains
        </Button>
        <Button variant="secondary" size="sm" onClick={exitAssessment}>
          Exit assessment
        </Button>
        <span className="survey-exit-name">
          {selectedSurvey?.name || "Assessment"}
        </span>
      </div>
      <div className="survey-workspace">
        {domainNavOpen ? (
          <button
            type="button"
            className="survey-domain-backdrop"
            aria-label="Close domains"
            onClick={closeDomainNav}
          />
        ) : null}

        <aside
          id="survey-domain-nav"
          className="survey-domain-nav sidebar-custom-scrollbar"
          aria-label="Assessment domains"
        >
          {hasValidCSFSurvey && (
            <CSFSidebar
              selectedSurvey={selectedSurvey}
              isAdminLogin={isAdminLogin}
              onSidebarSelect={handleCSFSelect}
              isActiveSurvey={activeSurveyStore === SURVEYS.CSF}
            />
          )}
          <hr className="sider-divider" />
          <CRRSidebar
            {...props}
            selectedSurvey={selectedSurvey}
            isAdminLogin={isAdminLogin}
            onSidebarSelect={handleCRRSelect}
            isActiveSurvey={activeSurveyStore === SURVEYS.CRR}
          />
        </aside>

        <div className="survey-container" onClick={domainNavOpen ? closeDomainNav : undefined}>
          {activeSurveyStore === SURVEYS.CSF && hasValidCSFSurvey && (
            <CSFSurvey
              {...props}
              isAdminView={isAdminLogin ? !surveyEditable : false}
              selectedSurvey={selectedSurvey}
            />
          )}

          {activeSurveyStore === SURVEYS.CRR && (
            <CRRSurvey
              {...props}
              selectedSurvey={selectedSurvey}
              isAdminView={
                isAdminLogin
                  ? selectedSurvey?.status === "In Progress"
                    ? !surveyEditable
                    : true
                  : false
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyHome;
